'use client';
import { useLayoutEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { isPluginOverlayRoute } from './sameDocumentNavigation';
import type { PluginUiListing } from './types';

/** The routes this app presents as OVERLAYS over whatever page the reader was on. Core routes are
 *  static; /p/<plugin> joins them only when that plugin's manifest listing declares `presentation:
 *  "overlay"`. The parallel slot cannot report whether it matched, so the address plus the same listing
 *  that built the primary navigation are the one synchronous answer. */
export const PAGE_OVERLAY_ROUTES = ['/account', '/settings'] as const;
export function isPageOverlayRoute(pathname: string | null, listing: PluginUiListing[] | undefined): boolean {
  if (PAGE_OVERLAY_ROUTES.some((route) => pathname === route || pathname?.startsWith(`${route}/`))) return true;
  return isPluginOverlayRoute(pathname, listing);
}

/** The path of the PAGE the chrome frames, which is not always the address in the bar.
 *
 *  `/settings` and `/account` are pages presented as overlays (`components/ui/PageOverlay.tsx`):
 *  opening one is a real navigation, so `usePathname()` answers the overlay while the page under it
 *  stays mounted. Every decision that arranges THAT page — the measure its frame reads at, the chat
 *  chrome, the chromeless terminal route, the route transition that holds its mounted state — has to
 *  keep reading the path it was framed under, or opening Settings visibly reflows and remounts the
 *  page behind the scrim: a workbench collapsing to the reading measure mid-animation.
 *
 *  While an overlay is up the page under it is inert (the overlay stack guarantees that) and cannot
 *  navigate, so the path held when the address turned into an overlay route stays true until the
 *  overlay closes and the address returns to a page — a plain "the last page address" is complete
 *  here, not a guess.
 *
 *  The overlay is recognised from the ADDRESS (`isPageOverlayRoute`) and not from the presence of the
 *  slot node: an unmatched parallel slot still renders its `default.tsx`, so `pageOverlay != null` is
 *  true on every route and pinned the first address the document ever loaded — leaving a workbench
 *  frame on every page navigated to afterwards until a reload. Reading the address answers in the same
 *  render that changed it, so there is no commit where the frame is measured for the wrong page. */
export function usePagePath(listing: PluginUiListing[] | undefined): string | null {
  const pathname = usePathname();
  const overlay = isPageOverlayRoute(pathname, listing);
  // Null until the document has been on a page: arriving straight at a known overlay has no page under it.
  // An unresolved /p route remains an ordinary page until its authenticated listing arrives. On a cold
  // load that prevents a needless remount; a shell link has already loaded the listing that built it.
  const [pagePath, setPagePath] = useState<string | null>(overlay ? null : pathname);
  useLayoutEffect(() => {
    if (!overlay) setPagePath(pathname);
  }, [overlay, pathname]);
  return overlay ? pagePath : pathname;
}
